import { DOCUMENT, Injectable, inject } from '@angular/core';

import { ApiService } from '@frontend/sports/common/api-utils';
import { BannerType } from '@frontend/sports/types/components/banners/models';
import { UserService } from '@frontend/sports/user/feature';
import { UserEvent } from '@frontend/vanilla/core';
import { Observable, catchError, map, of, shareReplay, startWith, switchMap } from 'rxjs';

import { EnhancedTeaserViewModel, TeasersResponse } from './teaser.models';
import { TeaserUtilsService } from './teaser.utils.service';

@Injectable({ providedIn: 'root' })
export class TeasersService {
    private apiService = inject(ApiService);
    private userService = inject(UserService);
    private teaserUtils = inject(TeaserUtilsService);

    private readonly _doc = inject(DOCUMENT);

    readonly data = this.userService.onAuthenticationChange$.pipe(
        startWith({} as UserEvent),
        switchMap(() => this.apiService.get<TeasersResponse>('teasers').pipe(catchError(() => of({ teasers: [] })))),
        switchMap((response) => this.enhance(response.teasers)),
        shareReplay(1),
    );

    enhance(teasers: EnhancedTeaserViewModel[]): Observable<EnhancedTeaserViewModel[]> {
        if (!teasers) {
            return of([]);
        }

        teasers = this.teaserUtils.filter(teasers);

        return this.teaserUtils.extend(teasers, this.pageWidth).pipe(
            map((extended) => {
                this.addJackpotDetails(extended);

                return this.addSlideshowHack(teasers);
            }),
        );
    }

    private get pageWidth(): number {
        return this._doc.body.clientWidth;
    }

    private addJackpotDetails(teasers: EnhancedTeaserViewModel[]): void {
        teasers.forEach((t) => {
            if (t.jackPot) {
                t.jackpotBanner = {
                    source: '', // TODO: what source??
                    type: BannerType.Jackpot,
                    icon: t.jackPot.icon,
                    title: t.title,
                    subtitle: t.subtitle,
                    background: '',
                    jackpotGroup: t.jackPot.jackpotGroup,
                    value: t.jackPot.value,
                    link: {
                        link: t.link,
                        target: t.target,
                        href: t.href ?? '',
                        popup: '',
                        attributes: {},
                    },
                };
            }
        });
    }

    private addSlideshowHack(teasers: EnhancedTeaserViewModel[]): EnhancedTeaserViewModel[] {
        const originalTeaserCount = teasers.length;
        const validTeaserCount = [2, 3];
        const needsDuplicationForMultiSlideshow = validTeaserCount.indexOf(originalTeaserCount) > -1;

        if (needsDuplicationForMultiSlideshow) {
            teasers = [...teasers, ...teasers];
        }

        teasers.forEach((teaser) => {
            teaser.originalTeasersCount = originalTeaserCount;
        });

        return teasers;
    }
}
